import React, { useState } from 'react';
import { useSelector } from "react-redux";
import { useAuthToken } from "../TokenContext";
import { NavHashLink } from 'react-router-hash-link';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useIntl } from "react-intl";

// Constants
const MAX_INVITES = 5;
const MIN_INVITES = 1;

const Clientinvite = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [tabValue, setTabValue] = useState(0);
  const token = useAuthToken();
  const { user } = useSelector(state => state.auth);
  const [invites, setInvites] = useState([{ emails: '' }]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const intl = useIntl();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setInvites([{ emails: '' }]);
    setError('');
    setSuccess('');
  };

  const validateEmails = (emails) => {
    // Check for duplicates
    const emailValues = emails.map(invite => invite.emails.toLowerCase());
    const uniqueEmails = new Set(emailValues);
    if (uniqueEmails.size !== emails.length) {
      return 'Duplicate email addresses are not allowed';
    }
    
    // Check for valid email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmails = emails.filter(invite => !emailRegex.test(invite.emails));
    if (invalidEmails.length > 0) {
      return 'Please enter valid email addresses';
    }
    
    return null;
  };

  const handleInputChange = (index, value) => {
    const newInvites = invites.map((invite, i) => 
      i === index ? { ...invite, emails: value } : invite
    );
    setInvites(newInvites);
    setError('');
  };

  const handleAddEmail = () => {
    if (invites.length >= MAX_INVITES) {
      setError(`Maximum ${MAX_INVITES} invites allowed`);
      return;
    }
    setInvites([...invites, { emails: '' }]);
    setError('');
  };

  const handleRemoveEmail = (index) => {
    if (invites.length <= MIN_INVITES) {
      setError('At least one email is required');
      return;
    }
    const newInvites = invites.filter((_, i) => i !== index);
    setInvites(newInvites);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const validationError = validateEmails(invites);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      // Choose the endpoint based on the selected tab
      const endpoint = tabValue === 0 
        ? process.env.REACT_APP_CUSTOMERADMIN_EMAILINVITE // Stakeholder
        : process.env.REACT_APP_CUSTOMERADMIN_EMAILINVITEMANAGER; // Manager
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          invite: invites,
          customer_id: user.data[0].customer_id
        })
      });

      const data = await response.json();

      if (data.status === 200) {
        setSuccess('Invite emails sent successfully');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.data || 'Failed to send invites');
      }
    } catch (error) {
      setError('Failed to send invites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has an active subscription
  const hasActiveSubscription = user.subscriptiondata && 
    user.subscriptiondata.length > 0 && 
    user.subscriptiondata[0].Status === 'active';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Fade in timeout={600}>
        <Box>
          {!hasActiveSubscription ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: isDarkMode ? 'grey.900' : '#fff3cd',
                border: `1px solid ${isDarkMode ? '#d39e00' : '#ffeaa7'}`,
                borderRadius: 2
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  color: isDarkMode ? '#ffc107' : '#856404' 
                }}
              >
                {intl.formatMessage({ id: "noActiveSubscriptionStart" })}
              </Typography>
              <Button
                variant="contained"
                component={NavHashLink}
                to="/subscriptions"
                sx={{
                  bgcolor: '#ffc107',
                  color: '#000',
                  '&:hover': {
                    bgcolor: '#e0a800'
                  }
                }}
              >
                {intl.formatMessage({ id: "subscribe" })}
              </Button>
            </Paper>
          ) : (
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: isDarkMode ? 'grey.900' : '#fff',
                boxShadow: isDarkMode 
                  ? '0 8px 32px rgba(0,0,0,0.5)' 
                  : '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              {/* Navigation Tabs */}
              <Paper
                elevation={0}
                sx={{
                  bgcolor: isDarkMode ? 'grey.800' : 'grey.50',
                  borderRadius: 0
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      fontSize: '1rem',
                      py: 2
                    },
                    '& .Mui-selected': {
                      bgcolor: isDarkMode ? 'primary.dark' : 'primary.main',
                      color: '#fff !important'
                    }
                  }}
                >
                  <Tab
                    icon={<PersonIcon />}
                    label="Stakeholder"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<GroupIcon />}
                    label="Manager"
                    iconPosition="start"
                  />
                </Tabs>
              </Paper>

              {/* Header Section */}
              <Box
                sx={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, #1e3c72, #2a5298)'
                    : 'linear-gradient(135deg, #00b4d8, #0077b6)',
                  p: 3,
                  color: '#fff'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {tabValue === 0 
                    ? "Enter Email to Invite Stakeholder" 
                    : "Enter Email to Invite Pod Managers"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Send invitation emails to expand your team
                </Typography>
              </Box>

              {/* Form Section */}
              <Box sx={{ p: 3 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Box sx={{ mb: 3 }}>
                    {invites.map((invite, index) => (
                      <Card
                        key={index}
                        elevation={0}
                        sx={{
                          mb: 2,
                          bgcolor: isDarkMode ? 'grey.800' : '#f8f9fa',
                          border: `1px solid ${isDarkMode ? 'grey.700' : '#e9ecef'}`,
                          '&:hover': {
                            boxShadow: isDarkMode 
                              ? '0 4px 12px rgba(255,255,255,0.1)' 
                              : '0 4px 12px rgba(0,0,0,0.1)'
                          },
                          transition: 'box-shadow 0.3s ease'
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <EmailIcon 
                              sx={{ 
                                color: isDarkMode ? 'primary.light' : 'primary.main',
                                fontSize: '1.5rem'
                              }} 
                            />
                            <TextField
                              fullWidth
                              type="email"
                              placeholder="Enter an Email"
                              value={invite.emails}
                              onChange={(e) => handleInputChange(index, e.target.value)}
                              required
                              variant="outlined"
                              error={Boolean(error && invite.emails === '')}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  bgcolor: isDarkMode ? 'grey.700' : '#fff'
                                }
                              }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {index === invites.length - 1 && invites.length < MAX_INVITES && (
                                <Tooltip title="Add another email">
                                  <IconButton
                                    onClick={handleAddEmail}
                                    sx={{
                                      bgcolor: isDarkMode ? 'success.dark' : 'success.main',
                                      color: '#fff',
                                      '&:hover': {
                                        bgcolor: isDarkMode ? 'success.darker' : 'success.dark'
                                      }
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {invites.length > MIN_INVITES && (
                                <Tooltip title="Remove this email">
                                  <IconButton
                                    onClick={() => handleRemoveEmail(index)}
                                    sx={{
                                      bgcolor: isDarkMode ? 'error.dark' : 'error.main',
                                      color: '#fff',
                                      '&:hover': {
                                        bgcolor: isDarkMode ? 'error.darker' : 'error.dark'
                                      }
                                    }}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  {/* Submit Button */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? null : <SendIcon />}
                      sx={{
                        minWidth: 200,
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        background: isDarkMode
                          ? 'linear-gradient(45deg, #1976d2, #1565c0)'
                          : 'linear-gradient(45deg, #1dbf73, #17a05d)',
                        boxShadow: '0 4px 15px rgba(29, 191, 115, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(29, 191, 115, 0.4)',
                          transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                          bgcolor: 'grey.400'
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Invite'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default Clientinvite;